import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type {
  ProviderInfo,
  ConnectorConfig,
  ConnectorStatus,
  TestResult,
} from '@/modules/connectors/types'

// 查询键
export const connectorKeys = {
  all: ['connector'] as const,
  providers: () => [...connectorKeys.all, 'providers'] as const,
  configs: (companyId: number) => [...connectorKeys.all, 'configs', companyId] as const,
  status: (companyId: number) => [...connectorKeys.all, 'status', companyId] as const,
}

// 获取所有已注册的 Provider 列表（含 config_schema）
export const useConnectorProviders = () => {
  return useQuery({
    queryKey: connectorKeys.providers(),
    queryFn: async () => {
      return api.get<ProviderInfo[]>('/connector-config/providers')
    },
    staleTime: 30 * 60 * 1000, // 30分钟，provider 列表很少变化
  })
}

// 获取公司所有 Connector 配置
export const useConnectorConfigs = (companyId: number | undefined) => {
  return useQuery({
    queryKey: connectorKeys.configs(companyId!),
    queryFn: async () => {
      return api.get<ConnectorConfig[]>(`/connector-config/${companyId}`)
    },
    enabled: !!companyId,
    staleTime: 60 * 1000,
  })
}

// 获取公司所有 Connector 的实时状态
export const useConnectorStatus = (companyId: number | undefined) => {
  return useQuery({
    queryKey: connectorKeys.status(companyId!),
    queryFn: async () => {
      return api.get<Record<string, ConnectorStatus>>(`/connector-config/${companyId}/status`)
    },
    enabled: !!companyId,
    refetchInterval: 30 * 1000, // 30秒轮询
    staleTime: 10 * 1000,
  })
}

// 保存 Connector 配置
export const useSaveConnectorConfig = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ companyId, config }: { companyId: number; config: Record<string, any> }) => {
      return api.post<{ success: boolean; config_id: number; operation: string }>(
        `/connector-config/${companyId}/save`,
        config
      )
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: connectorKeys.configs(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: connectorKeys.status(variables.companyId) })
    },
  })
}

// 测试 Connector 连接
export const useTestConnector = () => {
  return useMutation({
    mutationFn: async ({ companyId, config }: { companyId: number; config: Record<string, any> }) => {
      return api.post<TestResult>(`/connector-config/${companyId}/test`, config)
    },
  })
}

// 删除 Connector 配置
export const useDeleteConnectorConfig = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ companyId, provider }: { companyId: number; provider: string }) => {
      return api.delete(`/connector-config/${companyId}/${provider}`)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: connectorKeys.configs(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: connectorKeys.status(variables.companyId) })
    },
  })
}
